import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "@mui/lab";
import ClearIcon from "@mui/icons-material/Clear";
import {
  MapitPhoneInput,
  MapItTextField,
  ShortAddressCodeSearch,
} from "src/components/common";
import { omit, pick, set } from "lodash";
import { addWarehouseSchema, updateWarehouseSchema } from "./ValidationSchemas";
import { runJoiValidate } from "src/_utils";
import { get, cloneDeep } from "lodash";
import { ConfirmationDialog } from "src/components/common";
import { updateProfile } from "src/_store/entities/profile";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import { useDispatch, useSelector } from "react-redux";
import { useUpdateMerchantProfileMutation } from "src/_api/profileAccountApi";
import { ROLES } from "src/constants";
import { MapitShipCitiesAutocomplete } from "src/_modules/MapitShip/components";
import { useGetWarehouseByIdQuery } from "src/_api/warehouseApi";
import MapAddressCard from "src/_modules/common/MapAndLocations/MapLocation";

export default function WarehouseDialog({
  openWarehouse,
  pending,
  isEditing,
  warehouseId,
  onCreateWareHouse,
  onUpdateWareHouse,
  onDeleteWareHouse,
  onClose,
  error,
  isSuccess,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));

  
  const [validationErrors, setValidationsErrors] = useState(false);
  const [locationMissing, setLocationMissing] = useState(false);
  
  const { profile } = useSelector((state) => state.entities.profile);
  const isMapitShipOnly = profile?.merchant?.deliveryMethod?.length === 1 && profile?.merchant?.deliveryMethod?.includes("MAPIT_SHIP")
  const isAdmin = profile.role === ROLES.admin;

  const handleSubmit = () => {
    console.log(" handleSubmit called");
    console.log(" isEditing:", isEditing);

    setLocationMissing(false);
    setValidationsErrors(null);
    if (isEditing) {
      let payload = pick(cloneDeep(warehouse), [
        "id",
        "name",
        "address",
        "phone",
      ]);

      payload.warehouseId = payload.id;
      delete payload.id;

      payload = omit(payload, "address.location.type");

      if (isAdmin) {
        delete payload.warehouseId;
      }

      if (!Boolean(payload?.phone)) delete payload?.phone;

      if (!payload?.address?.details) {
        delete payload.address.details;
      }
      ////
      if (payload?.address?.shortAddress) {
        delete payload.address.shortAddress;
      }
      console.log(" Payload before validation:", payload);
      const errors = runJoiValidate(
        updateWarehouseSchema(isAdmin),
        payload,
        isAdmin,
      );

      console.log("Validation errors:", errors);
      if (errors) {
        if (errors?.address?.location?.coordinates) {
          setLocationMissing(true);
        }
        setValidationsErrors(errors);
        return;
      }
      onUpdateWareHouse(payload);
    } else {
      let payload = { ...warehouse };
      if (!Boolean(payload?.phone)) delete payload?.phone;

      console.log(payload);
      const errors = runJoiValidate(addWarehouseSchema(), payload);
      console.log(errors);
      if (errors) {
        if (errors.address?.location?.coordinates) {
          setLocationMissing(true);
        }
        setValidationsErrors(errors);
        return;
      }
      onCreateWareHouse(payload);
    }
  };

  //======= Assign Default Shipping Company =========
  const dispatch = useDispatch();

  const [
    updateMerchantProfile,
    {
      isLoading: updateProfileLoading,
      isSuccess: updateProfileIsSuccess,
      error: updateProfileError,
      reset: updateProfileReset,
    },
  ] = useUpdateMerchantProfileMutation();

  const assignDefaultShippingCompany = async (event) => {
    try {
      const checked = event.target.checked;
      let response = await updateMerchantProfile({
        defaultWarehouse: checked ? warehouse?.id : null,
      }).unwrap();

      if (response?.success) {
        //  profileRefetch()
        if (!!response?.body?.role) {
          dispatch(updateProfile({ ...profile, merchant: response?.body }));
        }
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (!openWarehouse) {
      updateProfileReset();
    }
  }, [openWarehouse]);

  //===============================================

  const { data: warehouseData, isLoading: warehouseLoading } =
    useGetWarehouseByIdQuery(warehouseId, {
      skip: !warehouseId,
    });

  const [warehouse, setWarehouse] = useState({
    name: "",
    phone: "",
    address: {
      city: "",
      details: "",
      location: {
        coordinates: [],
      },
    },
  });

  useEffect(() => {
    if (isEditing && warehouseData) {
      setWarehouse(warehouseData);
    } else {
      setWarehouse({
        name: "",
        phone: "",
        address: {
          city: "",
          details: "",
          location: {
            coordinates: [],
          },
        },
      });
    }
  }, [isEditing, warehouseData]);

  const warehouseRef = useRef(null);
  warehouseRef.current = warehouse;

  const handleChange = ({ target: { value, name } }) => {
    console.log("handleChange called with:", { name, value });
    console.log("warehouse before change:", warehouseRef.current);

    let payload = cloneDeep(warehouseRef.current);
    set(payload, name, value);

    console.log("warehouse after set:", payload);
    setWarehouse({ ...payload });
  };

  return (
    <Dialog
      maxWidth="md"
      dir={theme.direction}
      fullWidth
      fullScreen={fullScreen}
      open={openWarehouse}
      aria-labelledby="warehouse-dialog-title"
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {`${isEditing ? t("Update warehouse") : t("Add warehouse")}`}
        <IconButton onClick={onClose} color="primary">
          <ClearIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid container spacing={1} alignItems="center">
          {profile?.merchant?.role === ROLES.merchant && isEditing && !isMapitShipOnly && (
            <Grid item xs={12}>
              <Box display="flex" alignItems="center">
                <FormControlLabel
                  sx={{ color: "secondary.main" }}
                  control={
                    updateProfileLoading ? (
                      <CircularProgress size={20} sx={{ ml: 1, mr: 1 }} />
                    ) : (
                      <Checkbox
                        checked={
                          profile?.merchant?.defaultWarehouse === warehouse?.id
                        }
                        onChange={assignDefaultShippingCompany}
                      />
                    )
                  }
                  label={t("Default warehouse")}
                />
                {!!updateProfileIsSuccess && (
                  <Typography color="success.main">
                    {t("Updated successfully")}
                  </Typography>
                )}
                {!!updateProfileError && (
                  <Typography color="error">{t(updateProfileError)}</Typography>
                )}
              </Box>
            </Grid>
          )}
          {/* {
                        profile?.merchant?.deliveryMethod?.includes("MAPIT_SHIP") && */}
          <Grid item xs={12} md={12}>
            <ShortAddressCodeSearch
              mb={2}
              shortAddress={warehouse?.address?.shortAddress ?? ""}
              values={[
                "city",
                "area",
                "street",
                "buildingNumber",
                "postcode",
                "shortAddress",
              ]}
              onChange={handleChange}
              onLocationChange={(coords) => {
                setWarehouse((prev) => {
                  let payload = cloneDeep(prev);
                  set(payload, "address.location.coordinates", coords);
                  return payload;
                });
              }}
            />
          </Grid>
          {/* } */}
          <Grid item xs={12} md={6}>
            <MapItTextField
              name="name"
              placeholder={t("Name")}
              value={warehouse?.name ?? ""}
              handleChange={handleChange}
              error={get(validationErrors, "name")}
              label={t("Name")}
              size="small"
              variant="outlined"
              required={true}
              margin="dense"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MapitPhoneInput
              name="phone"
              value={warehouse.phone}
              handleChange={handleChange}
              error={get(validationErrors, "phone")}
              required={true}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MapItTextField
              label={t("State/Region")}
              name="address.state"
              value={warehouse?.address?.state ?? ""}
              handleChange={handleChange}
              error={get(validationErrors, "address.state")}
              size="small"
              variant="outlined"
              inputProps={{ maxLength: 40 }}
              margin="dense"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {/* <MapitShipCitiesAutocomplete
                            name="address.city"
                            value={warehouse?.address?.city ?? ""}
                            onChange={handleChange}
                            error={get(validationErrors, "address.city")}
                            label={t("City")} size="small" variant="outlined" required={true} margin="dense" /> */}
            {profile?.merchant?.deliveryMethod?.includes("MAPIT_SHIP") ||
            profile?.role === ROLES.admin ? (
              <MapitShipCitiesAutocomplete
                name="address.city"
                value={warehouse?.address?.city ?? ""}
                onChange={handleChange}
                error={get(validationErrors, "address.city")}
                label={t("City")}
                size="small"
                variant="outlined"
                required={true}
                margin="dense"
              />
            ) : (
              <MapItTextField
                name="address.city"
                placeholder={t("City")}
                value={warehouse?.address?.city ?? ""}
                handleChange={handleChange}
                error={get(validationErrors, "address.city")}
                label={t("City")}
                size="small"
                variant="outlined"
                required={true}
                margin="dense"
              />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <MapItTextField
              label={t("District")}
              name="address.area"
              value={warehouse?.address?.area ?? ""}
              handleChange={handleChange}
              error={get(validationErrors, "address.area")}
              size="small"
              variant="outlined"
              required={true}
              margin="dense"
              inputProps={{ maxLength: 40 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MapItTextField
              label={t("Street")}
              name="address.street"
              value={warehouse?.address?.street ?? ""}
              handleChange={handleChange}
              error={get(validationErrors, "address.street")}
              size="small"
              variant="outlined"
              required={true}
              margin="dense"
              inputProps={{ maxLength: 40 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MapItTextField
              label={t("Building No.")}
              name="address.buildingNumber"
              value={warehouse?.address?.buildingNumber ?? ""}
              handleChange={handleChange}
              error={get(validationErrors, "address.buildingNumber")}
              size="small"
              variant="outlined"
              required={false}
              margin="dense"
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MapItTextField
              label={t("Postcode")}
              name="address.postcode"
              value={warehouse?.address?.postcode ?? ""}
              handleChange={handleChange}
              error={get(validationErrors, "address.postcode")}
              size="small"
              variant="outlined"
              required={false}
              margin="dense"
              inputProps={{ maxLength: 10 }}
            />
          </Grid>

          <Grid item xs={12} md={12}>
            <MapItTextField
              name="address.details"
              value={warehouse?.address?.details ?? ""}
              handleChange={handleChange}
              error={get(validationErrors, "address.details")}
              size="small"
              variant="outlined"
              required={false}
              margin="dense"
              placeholder={t("addressDetailsPlaceholder")}
              multiline={isXsScreen}
              rows={isXsScreen ? 2 : undefined}
              inputProps={{
                maxLength: 40,
              }}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            {warehouseLoading ? (
              <Skeleton variant="rectangular" width="100%" height={400} />
            ) : (
              <MapAddressCard
                coordinates={warehouse?.address?.location?.coordinates}
                handleChange={({ target: { value } }) => {
                  handleChange({
                    target: { name: "address.location.coordinates", value },
                  });
                }}
                sx={{ height: 400 }}
              />
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Box>
          <LoadingButton
            color="main"
            variant="contained"
            disableElevation
            onClick={handleSubmit}
            loading={pending}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
            }}
          >
            {isEditing ? t("Edit") : t("Add")}
          </LoadingButton>

          {isEditing && !isAdmin ? (
            <ConfirmationDialog
              btnText={t("Delete")}
              title={t("Confirm Deletion")}
              content={t("Are you sure you want to delete it?")}
              confirmAction={() => onDeleteWareHouse(warehouse.id)}
              pendingDelete={pending}
              LoadingButtonProps={{
                sx: {
                  textTransform: "none",
                  fontWeight: "bold",
                  mx: 1,
                },
              }}
            />
          ) : (
            ""
          )}
        </Box>
        <Box flex={1} textAlign="center">
          {isSuccess && (
            <Typography color="green.main">{t("Done successfully")}</Typography>
          )}
          {locationMissing && (
            <Typography color="error">
              {t("Please enter the location on the map")}
            </Typography>
          )}
          {!!error && <Typography color="error">{t(error)}</Typography>}
        </Box>{" "}
        <Box mr="auto" />
        <Button
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: "bold",
          }}
          onClick={onClose}
        >
          {t("Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
